import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import { Alert, Button, Box, Skeleton } from '@mui/material'
import { Group as GroupIcon } from '@mui/icons-material'
import { clientsApi, Client } from '../services/api'
import { userAtom } from '../state/auth'
import { isAdminOrManager } from '../utils/roles'

export default function PendingAssignmentsBanner() {
  const navigate = useNavigate()
  const [user] = useAtom(userAtom)
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)

  // Only show for admins and managers
  const canSeeAssignments = isAdminOrManager(user)

  useEffect(() => {
    if (canSeeAssignments) {
      loadPendingAssignments()
    } else {
      setLoading(false)
    }
  }, [canSeeAssignments])

  const loadPendingAssignments = async () => {
    try {
      const pendingClients = await clientsApi.getNeedingTeamAssignment()
      setClients(pendingClients)
    } catch (error) {
      console.error('Failed to load pending team assignments:', error)
    } finally {
      setLoading(false)
    }
  }

  // Don't render if user can't see assignments or no pending assignments
  if (!canSeeAssignments) {
    return null
  }

  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="rectangular" height={52} sx={{ borderRadius: 1 }} />
      </Box>
    )
  }

  if (clients.length === 0) {
    return null
  }

  const clientCount = clients.length
  const message =
    clientCount === 1
      ? `1 client needs team assignment`
      : `${clientCount} clients need team assignment`

  return (
    <Alert
      severity="warning"
      icon={<GroupIcon />}
      sx={{ mb: 3 }}
      action={
        <Button
          color="inherit"
          size="small"
          onClick={() => navigate('/clients?filter=needsTeamAssignment')}
        >
          Assign Team
        </Button>
      }
    >
      {message}
    </Alert>
  )
}
